// Input Validation & Security Utilities
// Sanitization, XSS protection, and file validation

// Allowed audio file types
const ALLOWED_AUDIO_TYPES = [
  'audio/mpeg', // MP3
  'audio/mp3',
  'audio/wav',
  'audio/wave',
  'audio/x-wav',
  'audio/flac',
  'audio/x-flac',
  'audio/aiff',
  'audio/x-aiff',
  'audio/ogg',
  'audio/webm',
];

// Allowed file extensions
const ALLOWED_EXTENSIONS = ['.mp3', '.wav', '.flac', '.aiff', '.ogg', '.m4a', '.webm'];

// Max file size (500 MB)
const MAX_FILE_SIZE = 500 * 1024 * 1024;

/**
 * Sanitize string input to prevent XSS
 */
export const sanitizeString = (input: string): string => {
  if (typeof input !== 'string') {
    return '';
  }

  // Remove HTML tags
  const div = document.createElement('div');
  div.textContent = input;
  const sanitized = div.textContent || div.innerText || '';

  // Remove potentially dangerous characters
  return sanitized
    .replace(/[<>]/g, '') // Remove < and >
    .trim()
    .slice(0, 1000); // Limit length
};

/**
 * Validate audio file
 */
export const validateAudioFile = (file: File): { valid: boolean; error?: string } => {
  // Check file type
  if (!ALLOWED_AUDIO_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext))) {
    return {
      valid: false,
      error: 'Invalid file format. Allowed: MP3, WAV, FLAC, AIFF, OGG, M4A, WebM',
    };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File is too large. Maximum: ${formatBytes(MAX_FILE_SIZE)}`,
    };
  }

  // Check if file is empty
  if (file.size === 0) {
    return {
      valid: false,
      error: 'File is empty',
    };
  }

  return { valid: true };
};

/**
 * Validate file name
 */
export const validateFileName = (fileName: string): { valid: boolean; error?: string } => {
  if (!fileName || fileName.trim().length === 0) {
    return {
      valid: false,
      error: 'File name cannot be empty',
    };
  }

  if (fileName.length > 255) {
    return {
      valid: false,
      error: 'File name is too long (max. 255 characters)',
    };
  }

  // Check for dangerous characters
  const dangerousChars = /[<>:"/\\|?*\x00-\x1f]/;
  if (dangerousChars.test(fileName)) {
    return {
      valid: false,
      error: 'File name contains invalid characters',
    };
  }

  return { valid: true };
};

/**
 * Sanitize file name
 */
export const sanitizeFileName = (fileName: string): string => {
  return fileName
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '') // Remove dangerous characters
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .slice(0, 255) // Limit length
    .trim();
};

/**
 * Validate BPM value
 */
export const validateBPM = (bpm: number | string): { valid: boolean; error?: string } => {
  const numBPM = typeof bpm === 'string' ? parseInt(bpm, 10) : bpm;

  if (isNaN(numBPM) || numBPM < 1 || numBPM > 300) {
    return {
      valid: false,
      error: 'BPM must be between 1 and 300',
    };
  }

  return { valid: true };
};

/**
 * Validate musical key
 */
export const validateMusicalKey = (key: string): { valid: boolean; error?: string } => {
  const validKeys = [
    'C', 'C#', 'Db', 'D', 'D#', 'Eb', 'E', 'F', 'F#', 'Gb', 'G', 'G#', 'Ab', 'A', 'A#', 'Bb', 'B',
    'Cm', 'C#m', 'Dbm', 'Dm', 'D#m', 'Ebm', 'Em', 'Fm', 'F#m', 'Gbm', 'Gm', 'G#m', 'Abm', 'Am', 'A#m', 'Bbm', 'Bm',
  ];

  if (!key || !validKeys.includes(key.trim())) {
    return {
      valid: false,
      error: 'Invalid musical key',
    };
  }

  return { valid: true };
};

/**
 * Format bytes to human readable string
 */
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Sanitize tag input
 */
export const sanitizeTag = (tag: string): string => {
  return sanitizeString(tag)
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '')
    .slice(0, 50);
};

/**
 * Rate limiting helper (client-side)
 */
export class RateLimiter {
  private requests: Map<string, number[]> = new Map();
  private windowMs: number;
  private maxRequests: number;

  constructor(maxRequests: number, windowMs: number) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
  }

  canMakeRequest(key: string): boolean {
    const now = Date.now();
    const requests = this.requests.get(key) || [];
    
    // Remove old requests outside the window
    const validRequests = requests.filter(time => now - time < this.windowMs);
    
    if (validRequests.length >= this.maxRequests) {
      return false;
    }

    // Add current request
    validRequests.push(now);
    this.requests.set(key, validRequests);
    
    return true;
  }

  reset(key: string) {
    this.requests.delete(key);
  }
}

// Global rate limiter for API calls (10 requests per 10 seconds)
export const apiRateLimiter = new RateLimiter(10, 10000);

